import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import { motion } from 'framer-motion';

const Navigation: React.FC = () => {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const isRegistrationPage = location.pathname === '/register';
  const isBillingPage = location.pathname === '/billing';
  const isRegistrationSuccessPage = location.pathname === '/registration-success';
  const isTermsPage = location.pathname === '/terms';
  const isPrivacyPage = location.pathname === '/privacy';
  const isRefundPage = location.pathname === '/refund';
  const isTournamentRulesPage = location.pathname === '/tournament-rules';
  const isLiabilityWaiversPage = location.pathname === '/liability-waivers';
  const useTransparentHeader = isLandingPage || isRegistrationPage || isBillingPage || isRegistrationSuccessPage || isTermsPage || isPrivacyPage || isRefundPage || isTournamentRulesPage || isLiabilityWaiversPage;
  const [, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpandedSections, setMobileExpandedSections] = useState<Set<string>>(new Set());

  const navigation: { name: string; href: string; current: boolean }[] = [];

  const aboutDropdown = [
    { name: 'Our Story', href: '/about/story' },
    { name: 'Our Team', href: '/about/team' },
    { name: 'Mission & Values', href: '/about/mission' },
    { name: 'Awards & Recognition', href: '/about/awards' },
  ];

  const eventsDropdown = [
    { name: 'Corporate Events', href: '/events/corporate' },
    { name: 'Fundraising Events', href: '/events/fundraising' },
    { name: 'Golf Tournaments', href: '/events/tournaments' },
    { name: 'Private Events', href: '/events/private' },
  ];

  const handleDropdownClose = () => {
    setOpenDropdown(null);
  };

  const toggleMobileSection = (sectionName: string) => {
    setMobileExpandedSections(prev => {
      const newSections = new Set(prev);
      if (newSections.has(sectionName)) {
        newSections.delete(sectionName);
      } else {
        newSections.add(sectionName);
      }
      return newSections;
    });
  };

  const navBarClass = useTransparentHeader 
    ? `${isLandingPage ? 'bg-transparent' : 'bg-black'} absolute ${isLandingPage ? 'top-[15px]' : 'top-0'} left-0 right-0 z-50` 
    : 'bg-white shadow-sm relative';

  const textClass = useTransparentHeader ? 'text-white' : 'text-gray-900';

  return (
    <Disclosure as="nav" className={navBarClass}>
      {({ open }) => (
        <>
          <div className="max-w-12xl hold-space mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              {/* Logo - Crash Champions */}
              <div className="flex-shrink-0 flex items-center flex-1">
                <Link to="/" className="flex items-center">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center"
                  >
                    <img 
                      src={`${process.env.PUBLIC_URL}/assets/CrashChampions_Logo_Black.png`} 
                      alt="Crash Champions" 
                      className="h-10 md:h-12 lg:h-14 w-auto"
                    />
                  </motion.div>
                </Link>
              </div>

              {/* Desktop Navigation - Centered */}
              <div className="hidden md:flex items-center justify-center flex-1 desktop-nav-tablet-hide">
                <div className="flex items-baseline space-x-8">
                  {navigation.map((item) => (
                    <div key={item.name} className="relative group">
                      {(item.name === 'About' || item.name === 'Events') ? (
                        <button
                          className={`${
                            item.current
                              ? useTransparentHeader
                                ? 'hover:text-red-400'
                                : 'hover:text-red-500'
                              : `${textClass}`
                          } px-3 py-2 text-[17px] font-medium transition-colors duration-300 ease-out flex items-center gap-1`}
                          style={{
                            color: item.current 
                              ? '#e51c22' 
                              : useTransparentHeader ? 'white' : '#111827'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.color = '#c41e3a'}
                          onMouseOut={(e) => e.currentTarget.style.color = item.current ? '#e51c22' : (useTransparentHeader ? 'white' : '#111827')}
                        >
                          {item.name}
                          <span className="w-4 h-4 flex items-center justify-center text-sm">▼</span>
                        </button>
                      ) : (
                        <Link
                          to={item.href}
                          className={`${
                            item.current
                              ? useTransparentHeader
                                ? 'hover:text-red-400'
                                : 'hover:text-red-500'
                              : `${textClass}`
                          } px-3 py-2 text-[17px] font-medium transition-colors duration-300 ease-out`}
                          style={{
                            color: item.current 
                              ? '#e51c22' 
                              : useTransparentHeader ? 'white' : '#111827'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.color = '#c41e3a'}
                          onMouseOut={(e) => e.currentTarget.style.color = item.current ? '#e51c22' : (useTransparentHeader ? 'white' : '#111827')}
                        >
                          {item.name}
                        </Link>
                      )}
                      
                      {/* Dropdown Menu */}
                      {(item.name === 'About') && (
                        <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                          {aboutDropdown.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-red-50"
                              style={{
                                transition: 'background-color 0.3s ease-out'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.color = '#e51c22'}
                              onMouseOut={(e) => e.currentTarget.style.color = '#374151'}
                              onClick={handleDropdownClose}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                      
                      {(item.name === 'Events') && (
                        <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-out">
                          {eventsDropdown.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-red-50"
                              style={{
                                transition: 'background-color 0.3s ease-out'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.color = '#e51c22'}
                              onMouseOut={(e) => e.currentTarget.style.color = '#374151'}
                              onClick={handleDropdownClose}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
                
              {/* VISIT CRASH CHAMPIONS Button & Tablet Hamburger */}
              <div className="flex justify-end flex-1 items-center gap-4">
                {/* VISIT CRASH CHAMPIONS - Hidden on mobile, shown on tablet+ */}
                <div className="hidden md:block">
                  <a
                    href="https://crashchampions.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 flex items-center gap-2"
                    style={{backgroundColor: '#e51c22'}}
                  >
                    VISIT CRASH CHAMPIONS
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
                
                {/* Tablet Hamburger Menu (1024-1366px) */}
                <div className="hidden hamburger-tablet-show">
                  <Disclosure.Button 
                    className={`inline-flex items-center justify-center p-2 rounded-md border-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500 ${
                      useTransparentHeader ? 'border-white' : 'border-gray-700'
                    }`}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = useTransparentHeader ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <span className={`block h-6 w-6 flex items-center justify-center text-xl ${useTransparentHeader ? 'text-white' : 'text-gray-900'}`} aria-hidden="true">✕</span>
                    ) : (
                      <span className={`block h-6 w-6 flex items-center justify-center text-xl ${useTransparentHeader ? 'text-white' : 'text-gray-900'}`} aria-hidden="true">☰</span>
                    )}
                  </Disclosure.Button>
                </div>
              </div>

              {/* Mobile menu button - Bigger and positioned right */}
              <div className="md:hidden">
                <Disclosure.Button 
                  className={`inline-flex items-center justify-center p-2 rounded-md border-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500 ${
                    useTransparentHeader ? 'border-white' : 'border-gray-700'
                  }`}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = useTransparentHeader ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <span className={`block h-6 w-6 flex items-center justify-center text-xl ${useTransparentHeader ? 'text-white' : 'text-gray-900'}`} aria-hidden="true">✕</span>
                  ) : (
                    <span className={`block h-6 w-6 flex items-center justify-center text-xl ${useTransparentHeader ? 'text-white' : 'text-gray-900'}`} aria-hidden="true">☰</span>
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* Mobile & Tablet Navigation - Slide In Menu */}
          <Disclosure.Panel 
            as={motion.div}
            initial={false}
            animate={{ 
              x: open ? '0%' : '100%',
              transition: { 
                type: 'spring', 
                stiffness: 200, 
                damping: 25, 
                mass: 0.8
              }
            }}
            className="fixed top-0 right-0 h-full w-80 bg-black shadow-2xl z-50 overflow-y-auto"
          >
            {/* Close Button */}
            <div className="flex justify-end p-4">
              <Disclosure.Button className="text-white hover:text-red-400 focus:outline-none">
                <span className="h-8 w-8 flex items-center justify-center text-2xl" aria-hidden="true">✕</span>
              </Disclosure.Button>
            </div>

            {/* Navigation Items */}
            <div className="px-6 pb-6 space-y-4">
              {navigation.map((item) => (
                <div key={item.name}>
                  {(item.name === 'About' || item.name === 'Events') ? (
                    <div>
                      {/* Expandable Section Header */}
                      <button
                        onClick={() => toggleMobileSection(item.name)}
                        className={`w-full flex items-center justify-between py-3 text-lg font-medium text-white hover:text-red-400 transition-colors duration-300 ease-out ${
                          item.current ? 'text-red-400' : ''
                        }`}
                      >
                        {item.name}
                        <span 
                          className={`h-5 w-5 flex items-center justify-center text-sm transform transition-transform duration-300 ease-out ${
                            mobileExpandedSections.has(item.name) ? 'rotate-180' : ''
                          }`}
                        >▼</span>
                      </button>
                      
                      {/* Expandable Content */}
                      {mobileExpandedSections.has(item.name) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ 
                            duration: 0.4, 
                            ease: [0.25, 0.46, 0.45, 0.94]
                          }}
                          className="pl-4 space-y-2 overflow-hidden"
                        >
                          {(item.name === 'About' ? aboutDropdown : eventsDropdown).map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="block py-2 text-gray-300 hover:text-white transition-colors duration-300 ease-out"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`block py-3 text-lg font-medium transition-colors duration-300 ease-out ${
                        item.current
                          ? 'text-red-400'
                          : 'text-white hover:text-red-400'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}

              <Link
                to="/cif"
                className={`block py-3 text-lg font-medium transition-colors duration-300 ease-out ${
                  location.pathname.startsWith('/cif') ? 'text-red-400' : 'text-white hover:text-red-400'
                }`}
              >
                Champion It Forward
              </Link>
              
              {/* Mobile VISIT CRASH CHAMPIONS Button */}
              <div className="pt-6">
                <a
                  href="https://crashchampions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 w-full"
                  style={{backgroundColor: '#e51c22'}}
                >
                  VISIT CRASH CHAMPIONS
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
              
              {/* Social Media Icons */}
              <div className="pt-8 pb-4">
                <div className="flex justify-center space-x-4">
                  {/* Facebook */}
                  <a href="https://www.facebook.com/crashchampions" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                    <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  
                  {/* Instagram */}
                  <a href="https://www.instagram.com/crashchampions" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                    <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.621 5.367 11.988 11.988 11.988s11.987-5.367 11.987-11.988C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.596-3.205-1.532-.757-.937-1.205-2.187-1.205-3.457s.448-2.52 1.205-3.457c.757-.936 1.908-1.532 3.205-1.532s2.448.596 3.205 1.532c.757.937 1.205 2.187 1.205 3.457s-.448 2.52-1.205 3.457c-.757.936-1.908 1.532-3.205 1.532zm7.718 0c-1.297 0-2.448-.596-3.205-1.532-.757-.937-1.205-2.187-1.205-3.457s.448-2.52 1.205-3.457c.757-.936 1.908-1.532 3.205-1.532s2.448.596 3.205 1.532c.757.937 1.205 2.187 1.205 3.457s-.448 2.52-1.205 3.457c-.757.936-1.908 1.532-3.205 1.532z"/>
                      <rect x="6" y="6" width="12" height="12" rx="3" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <path d="M16.5 7.5h.01"/>
                    </svg>
                  </a>
                  
                  {/* LinkedIn */}
                  <a href="https://www.linkedin.com/company/crash-champions-llc/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                    <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  
                  {/* YouTube */}
                  <a href="https://www.youtube.com/@CrashChampions" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                    <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default Navigation;